import {IssueTypeToken} from '@github-ui/issue-type-token'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {IssueOpenedIcon} from '@primer/octicons-react'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {IssueTypeChangedEventNode} from './issue-viewer-view-query-types'
import {IssueEventRationale} from './IssueEventRationale'
import styles from './IssueTypeEvent.module.css'
import {TimelineRow} from './row/TimelineRow'

// RELAY-CLEANUP(IssueTypeChangedEvent): widens nullable fields so still-Relay parent data is assignable.
// Remove when the timeline pagination fragments leave Relay.
export type IssueTypeChangedEventFragment = RelayCompat<
  Pick<IssueTypeChangedEventNode, 'actor' | 'createdAt' | 'databaseId' | 'intent' | 'issueType' | 'prevIssueType'>
>

type IssueTypeChangedEventProps = {
  queryRef: IssueTypeChangedEventFragment
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
  repositoryNameWithOwner?: string
}

export function IssueTypeChangedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  repositoryNameWithOwner,
}: IssueTypeChangedEventProps) {
  const {actor, createdAt, issueType, prevIssueType, databaseId, intent} = queryRef

  if (!issueType) {
    return null
  }

  const highlighted = String(databaseId) === highlightedEventId

  const getTooltipText = (isTextTruncated: boolean) => {
    return isTextTruncated ? issueType.name : undefined
  }

  if (!prevIssueType) {
    return null
  }

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={IssueOpenedIcon}
    >
      <TimelineRow.Main>
        {`${LABELS.timeline.issueTypeChanged.leading}`}
        <div className={styles.issueTypeTokenWrapper}>
          <IssueTypeToken
            name={prevIssueType.name}
            color={prevIssueType.color}
            href={`/${repositoryNameWithOwner}/issues?q=type:"${prevIssueType.name}"`}
            getTooltipText={getTooltipText}
            size="small"
          />
        </div>
        {`${LABELS.timeline.issueTypeChanged.trailing}`}
        <div className={styles.issueTypeTokenWrapper}>
          <IssueTypeToken
            name={issueType.name}
            color={issueType.color}
            href={`/${repositoryNameWithOwner}/issues?q=type:"${issueType.name}"`}
            getTooltipText={getTooltipText}
            size="small"
          />
        </div>
        {intent?.rationale && (
          <IssueEventRationale
            rationale={intent.rationale}
            issueEventDatabaseId={String(databaseId)}
            target={issueType.name}
            analyticsAction="ISSUE_TYPE_CHANGED"
          />
        )}
      </TimelineRow.Main>
    </TimelineRow>
  )
}
